import {
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  EXTENDED_CHART_COLORS,
  FORMAT_THRESHOLDS,
} from './constants';
import { ChartDataPoint, TimeSeriesDataPoint } from './types';

export function formatNumber(
  num: number,
  options?: {
    decimals?: number;
    locale?: string;
  },
): string {
  const { decimals = 1, locale = DEFAULT_LOCALE } = options || {};

  if (num >= FORMAT_THRESHOLDS.billion) {
    return (
      (num / FORMAT_THRESHOLDS.billion).toFixed(decimals).replace(/\.0$/, '') +
      'B'
    );
  }
  if (num >= FORMAT_THRESHOLDS.million) {
    return (
      (num / FORMAT_THRESHOLDS.million).toFixed(decimals).replace(/\.0$/, '') +
      'M'
    );
  }
  if (num >= FORMAT_THRESHOLDS.thousand) {
    return (
      (num / FORMAT_THRESHOLDS.thousand).toFixed(decimals).replace(/\.0$/, '') +
      'K'
    );
  }
  return num.toLocaleString(locale);
}

/**
 * Format a number as currency
 */
export function formatCurrency(
  num: number,
  currencyCode: string = DEFAULT_CURRENCY,
  options?: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string {
  const {
    locale = DEFAULT_LOCALE,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options || {};

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

/**
 * Format a number as percentage
 */
export function formatPercentage(
  num: number,
  options?: {
    decimals?: number;
    includeSymbol?: boolean;
  },
): string {
  const { decimals = 1, includeSymbol = true } = options || {};
  const formatted = num.toFixed(decimals);
  return includeSymbol ? `${formatted}%` : formatted;
}

/**
 * Format a date string
 */
export function formatDate(
  date: string | Date,
  format?: 'short' | 'medium' | 'long' | 'full',
  locale: string = DEFAULT_LOCALE,
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
      });
    case 'medium':
      return dateObj.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'long':
      return dateObj.toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    case 'full':
      return dateObj.toLocaleDateString(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    default:
      return dateObj.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
      });
  }
}

/**
 * Generate color palette dynamically
 * Uses golden angle for good color distribution
 */
export function generateColors(
  count: number,
  options?: {
    startHue?: number;
    saturation?: number;
    lightness?: number;
  },
): string[] {
  const { startHue = 0, saturation = 70, lightness = 50 } = options || {};
  const colors: string[] = [];
  const goldenAngle = 137.5;

  for (let i = 0; i < count; i++) {
    const hue = (startHue + i * goldenAngle) % 360;
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return colors;
}

/**
 * Get colors for chart series
 * Uses default palette or generates if more colors needed
 */
export function getChartColors(count: number): string[] {
  if (count <= EXTENDED_CHART_COLORS.length) {
    return EXTENDED_CHART_COLORS.slice(0, count);
  }
  return generateColors(count);
}

/**
 * Aggregate data by day
 */
export function aggregateByDay<T extends { date: string; value: number }>(
  data: T[],
  aggregateFunc: 'sum' | 'avg' | 'min' | 'max' = 'sum',
): T[] {
  const grouped = data.reduce(
    (acc, item) => {
      const day = item.date.split('T')[0]; // Get just the date part
      if (!acc[day]) {
        acc[day] = { ...item, date: day, values: [] };
      }
      acc[day].values.push(item.value);
      return acc;
    },
    {} as Record<string, T & { values: number[] }>,
  );

  return Object.values(grouped).map((group) => {
    let value: number;
    const values = group.values;

    switch (aggregateFunc) {
      case 'avg':
        value = values.reduce((sum, v) => sum + v, 0) / values.length;
        break;
      case 'min':
        value = Math.min(...values);
        break;
      case 'max':
        value = Math.max(...values);
        break;
      case 'sum':
      default:
        value = values.reduce((sum, v) => sum + v, 0);
        break;
    }

    const { values: _, ...rest } = group;
    return { ...rest, value } as unknown as T;
  });
}

/**
 * Fill missing dates in time series data
 */
export function fillMissingDates(
  data: TimeSeriesDataPoint[],
  startDate: string,
  endDate: string,
  defaultValue: number = 0,
): TimeSeriesDataPoint[] {
  if (data.length === 0) return [];

  const filled: TimeSeriesDataPoint[] = [];
  const dataMap = new Map(data.map((d) => [d.date, d]));

  const start = new Date(startDate);
  const end = new Date(endDate);
  const current = new Date(start);

  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0];
    const existing = dataMap.get(dateStr);

    if (existing) {
      filled.push(existing);
    } else {
      filled.push({ date: dateStr, value: defaultValue });
    }

    current.setDate(current.getDate() + 1);
  }

  return filled;
}

/**
 * Calculate percentage change between two values
 */
export function calculatePercentageChange(
  current: number,
  previous: number,
): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

/**
 * Sort data by date
 */
export function sortByDate<T extends { date: string }>(
  data: T[],
  order: 'asc' | 'desc' = 'asc',
): T[] {
  return [...data].sort((a, b) => {
    const comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
    return order === 'asc' ? comparison : -comparison;
  });
}

/**
 * Calculate cumulative values
 */
export function calculateCumulative<T extends { value: number }>(
  data: T[],
): T[] {
  let cumulative = 0;
  return data.map((item) => {
    cumulative += item.value;
    return { ...item, value: cumulative };
  });
}

/**
 * Get top N items by value
 */
export function getTopItems(
  data: ChartDataPoint[],
  count: number,
  includeOthers: boolean = false,
): ChartDataPoint[] {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, count);

  if (includeOthers && sorted.length > count) {
    const others = sorted.slice(count);
    const othersSum = others.reduce((sum, item) => sum + item.value, 0);
    top.push({ label: 'Others', value: othersSum });
  }

  return top;
}

/**
 * Format value based on type
 */
export function formatValue(
  value: number,
  format: 'number' | 'percentage' | 'currency',
  currencyCode?: string,
): string {
  switch (format) {
    case 'percentage':
      return formatPercentage(value);
    case 'currency':
      return formatCurrency(value, currencyCode);
    case 'number':
    default:
      return formatNumber(value);
  }
}

/**
 * Check if device is mobile based on window width
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768; // md breakpoint
}

/**
 * Reduce data points for mobile devices
 */
export function reduceDataPoints<T>(data: T[], maxPoints: number = 20): T[] {
  if (data.length <= maxPoints) return data;

  const step = Math.ceil(data.length / maxPoints);
  return data.filter((_, index) => index % step === 0);
}
